<script setup>
import { Handle, Position } from '@vue-flow/core';
import { NodeToolbar } from '@vue-flow/node-toolbar';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { triggerNodeAction } from '../nodeEventBus';

const props = defineProps({
  id: { type: String, default: '' },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const isHovered = ref(false);
let hoverTimeout = null;
const onMouseEnter = () => {
  clearTimeout(hoverTimeout);
  isHovered.value = true;
};
const onMouseLeave = () => {
  hoverTimeout = setTimeout(() => {
    isHovered.value = false;
  }, 300);
};

const unitLabels = {
  seconds: 'FLOW_BUILDER.NODE_EDITOR.DELAY.UNITS.SECONDS',
  minutes: 'FLOW_BUILDER.NODE_EDITOR.DELAY.UNITS.MINUTES',
  hours: 'FLOW_BUILDER.NODE_EDITOR.DELAY.UNITS.HOURS',
  days: 'FLOW_BUILDER.NODE_EDITOR.DELAY.UNITS.DAYS',
};

const summary = () => {
  const value = props.data?.delay_value || 1;
  const unit = props.data?.delay_unit || 'seconds';
  const unitLabel = t(unitLabels[unit] || unitLabels.seconds);
  return `${value} ${unitLabel}`;
};
</script>

<template>
  <div
    class="flow-node min-w-[180px] max-w-[240px] bg-white dark:bg-n-slate-1 rounded-[10px] p-2 text-[10px] shadow-md hover:shadow-lg border transition-all relative"
    :class="
      selected
        ? 'border border-amber-500 shadow-lg'
        : 'border border-transparent hover:border-n-amber-5'
    "
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <NodeToolbar :is-visible="isHovered" :position="Position.Top" :offset="8">
      <div
        class="flex gap-1 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg p-1"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-blue-1 dark:hover:bg-n-blue-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DUPLICATE')"
          @click.stop="triggerNodeAction('duplicate', props.id)"
        >
          <span class="i-lucide-copy text-base text-n-blue-5" />
        </button>
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DELETE')"
          @click.stop="triggerNodeAction('delete', props.id)"
        >
          <span class="i-lucide-trash text-base text-n-ruby-5" />
        </button>
      </div>
    </NodeToolbar>
    <div class="flex items-center gap-2">
      <span class="i-lucide-clock text-[10px] text-n-amber-10" />
      <div class="flex flex-col min-w-0">
        <span class="font-semibold text-n-slate-12 text-[9px]">
          {{ t('FLOW_BUILDER.SIDEBAR.DELAY') }}
        </span>
        <span class="text-[8px] text-n-slate-11 truncate">
          {{ summary() }}
        </span>
      </div>
    </div>
    <Handle
      type="target"
      :position="Position.Left"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_INPUT')"
    />
    <Handle
      type="source"
      :position="Position.Right"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_OUTPUT')"
    />
  </div>
</template>
